import React from 'react';
import cn from '@/utils/cn';
import Text from '@/components/ui/Text';
import TCITooltip from '@/components/ui/Tooltip/TCITooltip';
import useFlags from '@/hooks/useFlags';
import assignmentLmsKind, { AssignmentLmsFields } from '@/components/shared/Lms/assignmentLmsKind';
import { lmsProviderConfig } from '@/components/shared/Lms/lmsProviders';
import { getLmsIconConfig } from './lmsIconConfigs';

interface LmsIconProps {
  assignment: AssignmentLmsFields & {
    has_sync_errors: boolean;
    not_finalized_on_lti: boolean;
    pending_publish_on_google?: boolean;
  };
}

const LmsIcon = ({ assignment }: LmsIconProps) => {
  const { updatedLtiOrphanedAssignmentFlow } = useFlags();
  const kind = assignmentLmsKind(assignment);

  if (!kind) return null;

  // AIDEV-NOTE: Only show orphaned assignment icons when feature flag is enabled
  const config = getLmsIconConfig({
    hasError: assignment.has_sync_errors,
    isGoogleDraft: !!assignment.pending_publish_on_google,
    isOrphaned: assignment.not_finalized_on_lti && updatedLtiOrphanedAssignmentFlow,
    kind,
  });

  if (config) {
    const { tooltip } = config;

    return (
      <TCITooltip side="right">
        <TCITooltip.Trigger asChild>
          <img alt={config.alt} className="ml10" src={config.logo} width="30" />
        </TCITooltip.Trigger>
        <TCITooltip.Content>
          <div className="tw-text-left">
            {tooltip.header && <Text weight="bold">{tooltip.header}</Text>}
            <Text className={cn({ 'tw-mt-5': tooltip.header })}>
              {tooltip.body}
              {tooltip.link && (
                <>
                  {assignment.has_sync_errors && <br />}
                  <a
                    className={cn({ 'tw-ml-1': !assignment.has_sync_errors })}
                    href={tooltip.link}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {tooltip.linkText}
                  </a>
                </>
              )}
            </Text>
          </div>
        </TCITooltip.Content>
      </TCITooltip>
    );
  }

  const { icons, name } = lmsProviderConfig(kind);

  return <img alt={`Connected to ${name}`} className="ml10" src={icons.success} width="30" />;
};

export default LmsIcon;
